import { Button } from '@components/Button/';
import EVImg from '@components/EVImg';
import AspectBox from '@components/Layout/AspectBox';
import Block from '@components/Layout/Block';
import { color, font, query, space } from '@design/templateFns';
import { ChevronLeft, ChevronRight } from '@icon/Chevrons';
import { download } from '@util/download';
import { FC, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

const Noop = () => null;

const Wrapper = styled.div`
  position: relative;
  z-index: 1;
  top: -39px;
  padding-top: 150px;
  padding-bottom: 0px;
  overflow: hidden;
  display: flex;
  gap: 20px;
  flex-direction: column-reverse;
  margin-left: ${space('marginMobile')};
  margin-right: ${space('marginMobile')};
  @media screen and (${query.atLeast('tablet')}) {
    gap: 157px;
    flex-direction: row;
    justify-content: flex-start;
    margin: 0 auto;
  }
  @media (max-width: 1200px) {
    gap: 40px;
  }
  @media (max-width: 991px) {
    padding-top: 0px;
    top: 0;
  }
`;

const ImageWrapper = styled.div<{ $active: boolean }>`
  min-width: ${({ $active }) => ($active ? '630px' : '430px')};
  min-height: ${({ $active }) => ($active ? '474px' : '324px')};
  transition: ${({ $active }) => ($active ? 'all 0.5s ease 1s' : 'all 0.5s ease')};
  width: 100%;
  overflow: hidden;

  @media (max-width: 991px) {
    min-width: unset;
    min-height: unset;
    transition: unset;
    height: auto;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  left: -10px;
  @media screen and (${query.atLeast('tablet')}) {
    justify-content: center;
    max-width: 430px;
    a {
      width: 120px;
    }
  }
  @media (max-width: 991px) {
    max-width: unset;
    width: 100%;
    left: 0px;
  }
`;

const Headline = styled.h2`
  ${font('headline-xs')};
  color: ${color('fg')};
  margin-bottom: 40px;
  padding: 0;
  text-align: start;
  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-md')};
    margin-bottom: 60px;
  }
  @media (max-width: 991px) {
    margin-bottom: 40px;
  }
  @media screen and (${query.below('desktop')}) {
    text-align: center;
  }
`;

const Eyebrow = styled.div`
  ${font('eyebrow-md')};
  font-size: 14px;
  text-transform: uppercase;
  margin-bottom: 16px;
  text-align: start;
  color: ${color('cta')};
  @media screen and (${query.below('desktop')}) {
    text-align: center;
  }
`;

const TextContainer = styled.div`
  border-left: 1px solid #c0c0c0;
  border-right: 1px solid #c0c0c0;
  border-bottom: 1px solid #c0c0c0;
  padding-top: 22px;
  padding-bottom: 22px;
  padding-left: 36px;
`;

const SlideTitle = styled.h3`
  ${font('headline-xxs')};
  color: ${color('fg')};
  padding: 0;
  margin-bottom: 32px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 1px;
    background-color: #c0c0c0;
    bottom: -16px;
    left: 0;
    z-index: 1;
  }
  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-xs')};
  }
`;

const SlideText = styled.p`
  ${font('body-md-light')};
  margin-bottom: 8px;
  margin-right: 25px;
`;

// For use in Story
export const StoryWrapper = styled.div`
  display: flex;
`;

//Carousel

const CarouselContainer = styled.div`
  width: 100%;
`;

const CarouselNav = styled.div`
  position: relative;
  z-index: 2;
  top: 45px;
  padding-left: 630px;
  display: flex;
  align-items: left;
  @media (max-width: 991px) {
    padding-left: 12px;
    top: 0;
    margin-bottom: 40px;
  }
  @media screen and (${query.below('desktop')}) {
    align-items: center;
    justify-content: center;
  }
`;

const CarouselArrow = styled.div<{ $right?: boolean }>`
  position: relative;
  margin-right: ${({ $right }) => ($right ? '5px' : '0')};
  margin-left: ${({ $right }) => ($right ? '0' : '5px')};
  height: 50px;
  width: 50px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid #e5e5e5;
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }
`;

export const ListContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 0;
  padding-right: 0;

  .slick-list {
    width: 430px;
    @media (max-width: 991px) {
      width: 100%;
    }
  }

  .slick-track {
    display: flex;
    flex-wrap: nowrap;
    z-index: 4;
  }

  .slick-slide {
    display: flex;
    position: relative !important;
    top: 0;
    transition: all 0.5s ease;
    min-width: 430px;
    max-width: 430px;
    a,
    button {
      display: none;
      @media (max-width: 991px) {
        display: inline-block;
      }
    }

    @media (max-width: 991px) {
      min-width: unset;
      max-width: unset;
      width: 100%;
      padding: 0 12px;
    }
  }

  .slick-slide.slick-active.slick-current {
    transition: all 0.5s ease 1s;
    min-width: 630px;
    max-width: 630px;
    top: -145px;
    a,
    button {
      display: inline-block;
    }
    @media (max-width: 991px) {
      min-width: unset;
      max-width: unset;
      width: 100%;
      top: 0px;
      transition: unset;
    }
  }

  .slick-slide > div {
    display: flex;
    flex: 0 0 100%;
    transform: scale(0.95);
    @media (max-width: 991px) {
      transform: scale(1);
    }
  }

  .slick-dots {
    display: none;
  }

  .slick-dots li button {
    display: none;
  }
`;

export const CarouselSlot = styled.span`
  position: relative !important;
`;

const SlideBlock = styled.div`
  @media (max-width: 576px) {
    width: 100%;
    height: auto;
  }
`;

/**
 * Renders a carousel component displaying value prop cards.
 * This code defines a functional component ValuePropCarouselAlt that renders a carousel displaying value prop cards. It uses state and refs to handle carousel navigation. The component  maps over valuePropCardContentAlt to render each card with an image, title, description, and optional download or external link button. The carousel has navigation arrows, autoplay, and  responsive settings. It also includes a getExtension function to extract file extensions from URLs and a downloadHandler function to handle file downloads.
 * @return {ReactNode} The rendered carousel component.
 */

export interface ValuePropCarouselAltProps {
  eyebrow: string;
  header: string;
  backgroundColor: boolean;
  valuePropCardContentAlt: any;
}

export const ValuePropCarouselAlt: FC<ValuePropCarouselAltProps> = ({
  eyebrow,
  header,
  backgroundColor,
  valuePropCardContentAlt,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const ref = useRef({
    slickNext: () => {},
    slickPrev: () => {},
    innerSlider: {
      state: {
        currentSlide: 1,
      },
    },
  });

  const onPrev = (e) => {
    e.preventDefault();
    ref.current.slickPrev();
  };

  const onNext = (e) => {
    e.preventDefault();
    ref.current.slickNext();
  };

  const SliderConfig = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    prevArrow: <Noop />,
    nextArrow: <Noop />,
    easing: 'ease',
    slidesToScroll: 1,
    mobileFirst: true,
    autoplay: true,
    pauseOnHover: true,
    beforeChange: (_current, next) => {
      setActiveSlide(next);
    },
    responsive: [
      {
        breakpoint: 991,
        settings: {
          autoplay: false,
        },
      },
    ],
  };

  /**
   *  Returns the file extension of a given URL.
   *  This code defines a function getExtension that extracts the file extension from a given URL by finding the last dot in the URL and
   *  returning the substring after it.
   *  @param {string} url - The URL to extract the file extension from.
   *  @return {string} The file extension of the URL, or an empty string if no extension is found.
   */
  const getExtension = (url) => {
    const lastDotIndex = url.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '';
    }
    return url.substring(lastDotIndex + 1);
  };

  const downloadHandler = async (url, fileName) => {
    await download(url, fileName);
  };

  /**
   * A function that truncates text if it exceeds a maximum length.
   *
   * @param {string} text - The text to truncate.
   * @return {string} The truncated text.
   */
  function truncateText(text) {
    if (!text) return '';
    const maxLength = 80;
    if (text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  }

  return (
    <Block fullOnMobile background={backgroundColor === true ? 'secondaryBg' : 'bg'}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Headline>{header}</Headline>
      <CarouselNav>
        <CarouselArrow onClick={(e) => onPrev(e)} $right={true}>
          <ChevronLeft color={'#383737'} />
        </CarouselArrow>
        <CarouselArrow onClick={(e) => onNext(e)}>
          <ChevronRight color={'#383737'} />
        </CarouselArrow>
      </CarouselNav>
      <Wrapper>
        <CarouselWrapper>
          <CarouselContainer>
            <ListContainer ref={ref} as={Slider} {...SliderConfig}>
              {valuePropCardContentAlt &&
                valuePropCardContentAlt.map(function (slide, index) {
                  return (
                    <CarouselSlot key={index}>
                      <SlideBlock>
                        <ImageWrapper $active={activeSlide === index ? true : false}>
                          <AspectBox $ratio={1.3333}>
                            <EVImg
                              src={slide.cardImage}
                              sizes={['516px', '516px', '516px', '516px']}
                              unoptimized={true}
                              objectFit="contain"
                            />
                          </AspectBox>
                        </ImageWrapper>
                        <TextContainer>
                          <SlideTitle>{slide.cardHeadline}</SlideTitle>
                          <SlideText>
                            {activeSlide === index
                              ? slide.cardDescription
                              : truncateText(slide.cardDescription)}
                          </SlideText>
                          {slide?.links &&
                            slide?.links.map((link, index) => {
                              //check if the contentType is linkDownload
                              if (link?.sys?.contentType?.sys?.id === 'linkDownload') {
                                return (
                                  <Button
                                    arrow={true}
                                    key={index}
                                    variant={'tertiaryBlack'}
                                    label={'Download Now'}
                                    target={link?.fields?.external}
                                    onClickHandler={() =>
                                      downloadHandler(
                                        'https:' +
                                          link?.fields?.downloadableAsset?.fields?.file?.url,
                                        `${
                                          link?.fields?.downloadableAsset?.fields?.title
                                        }.${getExtension(
                                          link?.fields?.downloadableAsset?.fields?.file?.url,
                                        )}`,
                                      )
                                    }
                                  />
                                );
                              } else {
                                return (
                                  <div key={index}>
                                    <Button
                                      arrow={true}
                                      variant={'tertiaryBlack'}
                                      target={link?.fields?.external}
                                      url={link?.fields?.url}
                                      label={link?.fields?.text}
                                    />
                                  </div>
                                );
                              }
                            })}
                        </TextContainer>
                      </SlideBlock>
                    </CarouselSlot>
                  );
                })}
            </ListContainer>
          </CarouselContainer>
        </CarouselWrapper>
      </Wrapper>
    </Block>
  );
};

export default ValuePropCarouselAlt;
